import { Flex, Form, Typography } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { CONTRACT_BLOCK_ACTION_MODE } from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useMemo, useState } from "react";

import { blockContract, unblockContract } from "services";
import { useStores } from "stores";
import { CustomButton, DefaultTextArea } from "ui";
import { requestWithNotify } from "utils";
import { _isNotLongerThan, _isValidCommentText } from "utils/form/simple-validation";

interface ContractBlockActionModalProps {
  mode: CONTRACT_BLOCK_ACTION_MODE;
}

export const ContractBlockActionModal = observer(({ mode }: ContractBlockActionModalProps) => {
  const { actionModals, contractCard } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const comment = Form.useWatch("comment", form);

  const isBlockMode = mode === CONTRACT_BLOCK_ACTION_MODE.BLOCK;

  const disabled = useMemo(() => {
    return loading;
  }, [loading]);

  const closeModal = () => {
    actionModals.closeAll();
  };

  const onFinish = async () => {
    setLoading(true);

    const formattedComment = String(comment || "").trim();

    try {
      const res = await requestWithNotify({
        fetchFn: () =>
          isBlockMode
            ? blockContract({ id: contractCard.card.id }, { comment: formattedComment })
            : unblockContract({ id: contractCard.card.id }),
        openNotification,
        successMsg: isBlockMode ? "Договор успешно заблокирован" : "Договор успешно разблокирован",
      });

      if (!res.result) {
        return;
      }

      closeModal();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex vertical gap="large">
      <Form form={form} name={`contractStatusAction-${mode}`} onFinish={onFinish}>
        {isBlockMode ? (
          <Form.Item
            name="comment"
            rules={[
              _isNotLongerThan(150, "Причина блокировки не должна быть длиннее 150 символов"),
              _isValidCommentText("Причина блокировки не должна содержать специальные символы"),
            ]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <DefaultTextArea placeholder="Причина блокировки..." maxLength={150} rows={4} disabled={loading} />
          </Form.Item>
        ) : (
          <Typography.Text>Вы уверены, что хотите разблокировать договор?</Typography.Text>
        )}

        <Form.Item className="margin-top-lg">
          <Flex gap="large">
            <CustomButton htmlType="submit" disabled={disabled} loading={loading} className="full-width">
              Подтвердить
            </CustomButton>
            <CustomButton
              htmlType="button"
              type="default"
              disabled={loading}
              loading={loading}
              onClick={closeModal}
              className="full-width"
            >
              Отмена
            </CustomButton>
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  );
});
